import { Component } from '@theme/component';
import { SlideshowSelectEvent } from '@theme/events';

/**
 * Tab bar that swaps which collection's product carousel is visible below it.
 * Every tab's carousel is server-rendered up front; switching tabs only toggles
 * which panel is visible, so each `slideshow-component` stays mounted and simply
 * resumes its own resize/intersection observers once shown.
 *
 * Pagination dots are generated here rather than reusing the slideshow's own
 * per-slide dots: how many products are visible at once changes per breakpoint
 * (columns setting on desktop, one card on mobile), so the number of dots has
 * to reflect pages of visible products, not one dot per product. `Slideshow`
 * already exposes `visibleSlides`/`slides`/`current`/`select()` as public API
 * and fires `SlideshowSelectEvent` on every slide change — the same contract
 * `collection-links.js` builds on — so this stays a consumer of that API
 * instead of a change to the shared component.
 *
 * How many products are visible at once is the same for every tab at a given
 * viewport (they all share the section's columns setting/CSS breakpoint), so
 * only the currently active carousel ever needs to measure it — every other
 * tab's dots reuse that shared count instead of waiting for their own,
 * still-hidden `Slideshow` to remeasure once revealed. That per-tab wait was
 * what caused the dots to flash "all products" before collapsing down to the
 * right count on every tab switch.
 *
 * @typedef {Object} Refs
 * @property {HTMLButtonElement[]} tabs
 * @property {HTMLElement[]} panels
 * @property {import('slideshow').Slideshow[]} [carousels]
 * @property {HTMLElement[]} [pageDotsBars]
 *
 * @extends {Component<Refs>}
 */
export class CarouselTabsComponent extends Component {
  requiredRefs = ['tabs', 'panels'];

  /** @type {ResizeObserver} */
  #resizeObserver;

  /** Shared count of products visible at once, across every tab. */
  #perPage = 1;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);

    this.#resizeObserver = new ResizeObserver(() => this.#measurePerPage());
    this.#resizeObserver.observe(this);

    this.#measurePerPage();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
  }

  /**
   * @param {number} tabIndex - The index of the tab to select.
   */
  selectTab(tabIndex) {
    const { tabs, panels } = this.refs;
    const index = String(tabIndex);

    tabs.forEach((tab) => {
      const selected = tab.dataset.tabIndex === index;
      tab.classList.toggle('carousel-tabs__tab--active', selected);
      tab.setAttribute('aria-selected', String(selected));
    });

    panels.forEach((panel) => {
      panel.classList.toggle('carousel-tabs__panel--active', panel.dataset.tabIndex === index);
    });

    // `#perPage` is already known (it doesn't change on tab switch, only on
    // resize), so the newly revealed panel's dots render correctly right away.
    this.#renderAllPageDots();
  }

  /**
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = (event) => {
    const carousel = /** @type {import('slideshow').Slideshow} */ (event.target);
    const dotsBar = this.#dotsBarFor(carousel);
    if (!dotsBar) return;

    this.#renderPageDots(carousel, dotsBar);
  };

  /**
   * Measures how many products are visible at once from the currently active
   * carousel (the only one guaranteed to not be `display: none`), retrying
   * across frames since `Slideshow` measures this asynchronously. Once known,
   * applies it to every tab's dots — including tabs never yet shown.
   *
   * @param {number} [attempt]
   */
  #measurePerPage(attempt = 0) {
    const activeCarousel = this.#activeCarousel();
    if (!activeCarousel) return;

    const measured = activeCarousel.visibleSlides?.length ?? 0;

    if (measured > 0) {
      this.#perPage = measured;
      this.#renderAllPageDots();
      return;
    }

    if (attempt >= 30) return;
    requestAnimationFrame(() => this.#measurePerPage(attempt + 1));
  }

  /**
   * @returns {import('slideshow').Slideshow | undefined}
   */
  #activeCarousel() {
    const { panels, carousels } = this.refs;
    if (!panels || !carousels) return undefined;

    const activeIndex = panels.findIndex((panel) => panel.classList.contains('carousel-tabs__panel--active'));
    return carousels[activeIndex];
  }

  #renderAllPageDots() {
    const { carousels, pageDotsBars } = this.refs;
    if (!carousels || !pageDotsBars) return;

    carousels.forEach((carousel, i) => this.#renderPageDots(carousel, pageDotsBars[i]));
  }

  /**
   * @param {import('slideshow').Slideshow} carousel
   * @returns {HTMLElement | undefined}
   */
  #dotsBarFor(carousel) {
    const { carousels, pageDotsBars } = this.refs;
    if (!carousels || !pageDotsBars) return undefined;

    return pageDotsBars[carousels.indexOf(carousel)];
  }

  /**
   * @param {import('slideshow').Slideshow} carousel
   * @param {HTMLElement} [dotsBar]
   */
  #renderPageDots(carousel, dotsBar) {
    if (!carousel || !dotsBar) return;

    const perPage = Math.max(this.#perPage, 1);
    const totalSlides = carousel.slides?.length ?? 0;
    const pageCount = totalSlides ? Math.ceil(totalSlides / perPage) : 0;

    if (dotsBar.childElementCount !== pageCount) {
      dotsBar.replaceChildren();

      for (let page = 0; page < pageCount; page++) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'carousel-tabs__pagedot';
        button.setAttribute('aria-label', `Ir para o grupo ${page + 1} de produtos`);
        button.addEventListener('click', () => carousel.select(page * perPage));
        dotsBar.append(button);
      }
    }

    if (pageCount <= 1) return;

    const activePage = Math.floor((carousel.current || 0) / perPage);

    dotsBar.querySelectorAll('.carousel-tabs__pagedot').forEach((dot, i) => {
      dot.setAttribute('aria-selected', String(i === activePage));
    });
  }
}

if (!customElements.get('carousel-tabs-component')) {
  customElements.define('carousel-tabs-component', CarouselTabsComponent);
}
